import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Tag, Layers, Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import GuideBreadcrumb from "@/components/GuideBreadcrumb";
import LanguageSelector from "@/components/LanguageSelector";

export default function CodiciCpv() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Guida ai Codici CPV per gli Appalti Europei"
        description="Scopri come usare i codici CPV per trovare bandi europei. Guida completa alla classificazione degli appalti pubblici UE con esempi pratici."
        path="/it/guide/codici-cpv"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/guide/codici-cpv" },
          { lang: "x-default", href: "https://tedmonitor.eu/it/guide/codici-cpv" },
        ]}
      />
      <BreadcrumbJsonLd
        items={[
          { name: "Home", href: "https://tedmonitor.eu/" },
          { name: "Guide", href: "https://tedmonitor.eu/it/guide/consultare-ted" },
          { name: "Codici CPV", href: "https://tedmonitor.eu/it/guide/codici-cpv" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <GuideBreadcrumb
          items={[
            { label: "Guide", href: "/it/guide/consultare-ted" },
            { label: "Codici CPV" },
          ]}
        />
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="mb-4">
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                Guida
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Guida ai Codici CPV per gli Appalti Europei
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              I codici CPV (Common Procurement Vocabulary) sono il sistema di classificazione standard utilizzato
              dall'Unione Europea per categorizzare tutti gli appalti pubblici. Comprendere come funzionano è
              fondamentale per cercare e monitorare efficacemente i bandi europei.
            </p>

            {/* Internal links */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/guide/consultare-ted"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Come consultare il TED
              </Link>
              <Link
                to="/it/guide/bandi-vs-preinformazione"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Bandi vs Pre-informazione
              </Link>
              <Link
                to="/it/guide/filtri-ricerca-avanzata"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Filtri di ricerca avanzata
              </Link>
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio appalti europei
              </Link>
            </div>
          </div>
        </section>

        {/* Cosa sono i CPV */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Tag className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Cosa sono i codici CPV</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Il CPV (Common Procurement Vocabulary) è un sistema di classificazione a 8 cifre adottato dall'UE per
              standardizzare la descrizione degli oggetti degli appalti pubblici in tutte le lingue europee.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Ogni codice CPV identifica:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Una categoria merceologica specifica (lavori, forniture o servizi)",
                "Un livello di dettaglio progressivo (divisione → gruppo → classe → categoria)",
                "Una descrizione multilingue uniforme in tutte le lingue UE",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Struttura del codice */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Layers className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Struttura del codice CPV</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Un codice CPV è composto da 8 cifre + 1 cifra di controllo. Le prime cifre indicano categorie sempre più
              specifiche:
            </p>

            <div className="bg-background rounded-lg border p-4 mb-4">
              <p className="font-mono text-lg text-primary mb-2">72000000-5</p>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p><span className="font-medium">72</span> = Divisione (Servizi informatici)</p>
                <p><span className="font-medium">720</span> = Gruppo (Servizi di consulenza informatica)</p>
                <p><span className="font-medium">7200</span> = Classe</p>
                <p><span className="font-medium">72000</span> = Categoria</p>
                <p><span className="font-medium">-5</span> = Cifra di controllo</p>
              </div>
            </div>

            <p className="text-muted-foreground mb-3 text-sm">Esempi di divisioni principali:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                { code: "45", desc: "Lavori edili" },
                { code: "72", desc: "Servizi IT" },
                { code: "33", desc: "Dispositivi medici" },
                { code: "50", desc: "Riparazione e manutenzione" },
                { code: "79", desc: "Servizi alle imprese" },
                { code: "90", desc: "Servizi ambientali" },
              ].map((item) => (
                <div key={item.code} className="p-2 bg-background rounded-lg border">
                  <p className="font-mono text-primary text-sm">{item.code}xxxxxx</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Come usarli nella ricerca */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Search className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Come usare i CPV nella ricerca</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Usare i codici CPV correttamente ti permette di trovare bandi pertinenti anche quando sono scritti in
              lingue che non conosci. Le strategie di ricerca più efficaci includono:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Partire da codici più generici (prime 2-4 cifre) per esplorare una categoria",
                "Restringere a codici specifici (8 cifre) per risultati mirati",
                "Combinare più codici CPV per coprire settori correlati",
                "Salvare i codici CPV rilevanti per monitoraggi ricorrenti",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="mt-4">
              <Link to="/it/monitoraggio-bandi-ue" className="text-primary hover:underline font-medium text-sm">
                Scopri come monitorare per CPV con TED Monitor →
              </Link>
            </p>
          </div>
        </section>

        {/* CPV su TED Monitor */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Filter className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Filtri CPV su TED Monitor</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor ti permette di configurare monitoraggi basati su codici CPV specifici. Puoi:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Cercare codici CPV con autocompletamento nella tua lingua",
                "Combinare più CPV in un singolo monitoraggio",
                "Ricevere alert automatici quando escono bandi nei tuoi CPV target",
                "Visualizzare statistiche di mercato per categoria CPV",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Pronto a monitorare i bandi per codici CPV?
            </h2>
            <p className="text-base mb-6 opacity-90">
              TED Monitor ti permette di impostare monitoraggi mirati per CPV e ricevere alert automatici sui nuovi
              bandi europei.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/it/fonte-dei-dati" className="text-muted-foreground hover:text-foreground">
                Fonte dati
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">
                Termini
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
